import { z } from 'zod'

import { validTimestampSchema } from './conversations-contracts'

export const actionRequestTypeSchema = z.enum([
  'APPOINTMENT_REQUEST',
  'CALLBACK_REQUEST',
  'CANCELLATION_REQUEST',
  'RESCHEDULE_REQUEST',
])

export const interactionOperationalStateSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    unreadForCaller: z.boolean(),
    readRevision: z.number().int().nonnegative(),
    lastReadActivityAtMs: validTimestampSchema.nullable(),
    reviewStatus: z.enum(['REVIEWED', 'NEEDS_REVIEW']),
    reviewRevision: z.number().int().nonnegative(),
    reviewedAtMs: validTimestampSchema.nullable(),
    reviewedThroughActivityAtMs: validTimestampSchema.nullable(),
    starred: z.boolean(),
    starRevision: z.number().int().nonnegative(),
    unresolvedActionRequestCount: z.number().int().nonnegative().max(100),
    unresolvedActionRequestTypes: z.array(actionRequestTypeSchema).max(4),
    needsAction: z.boolean(),
  })
  .strict()

export const interactionOperationalCommandResultSchema =
  interactionOperationalStateSchema.extend({
    replayClassification: z.enum(['APPLIED', 'EXACT_REPLAY', 'NO_CHANGE']),
  })

export const interactionOperationalQuerySchema = z
  .object({
    schemaVersion: z.literal(1),
    items: z.array(interactionOperationalStateSchema).max(100),
  })
  .strict()

// The Action Requests behind one aggregate's needsAction. Staff complete one
// through the resolve command and correct a completion through reopen; review
// is a separate record. Revisions only grow: odd = OPEN, even = RESOLVED.
const actionRequestFields = {
  schemaVersion: z.literal(1),
  actionRequestId: z.string().regex(/^dar_v1_[a-f0-9]{64}$/),
  requestType: actionRequestTypeSchema,
  status: z.enum(['OPEN', 'RESOLVED']),
  createdAtMs: validTimestampSchema,
  updatedAtMs: validTimestampSchema,
  revision: z.number().int().positive(),
  sourceChannel: z.enum(['SMS', 'VOICE']),
  resolution: z
    .object({
      resolutionType: z.enum([
        'STAFF_HANDLED',
        'NO_ACTION_REQUIRED',
        'DUPLICATE',
      ]),
      resolvedAtMs: validTimestampSchema,
    })
    .strict()
    .nullable(),
  // Absent from servers that predate reopening: never reopened.
  reopenedAtMs: validTimestampSchema.nullable().optional(),
  // Whether staff can still reopen it. Absent from servers that predate
  // reopening: never reopenable.
  reopenable: z.boolean().optional(),
}

function lifecycleConsistent(value: {
  status: 'OPEN' | 'RESOLVED'
  revision: number
  resolution: unknown
  reopenedAtMs?: number | null
  reopenable?: boolean
}) {
  const reopenedAtMs = value.reopenedAtMs ?? null
  return value.status === 'OPEN'
    ? value.revision % 2 === 1 &&
        value.resolution === null &&
        (value.revision === 1) === (reopenedAtMs === null) &&
        value.reopenable !== true
    : value.revision % 2 === 0 &&
        value.resolution !== null &&
        reopenedAtMs === null
}

export const interactionActionRequestSchema = z
  .object(actionRequestFields)
  .strict()
  .refine(lifecycleConsistent, 'Action Request lifecycle is inconsistent')

export const interactionActionRequestListSchema = z
  .object({
    schemaVersion: z.literal(1),
    aggregateId: z.string().regex(/^ia_v1_[0-9a-f]{64}$/),
    items: z.array(interactionActionRequestSchema).max(100),
    // Whether staff may complete, undo and reopen in this clinic. Off unless
    // the server says so.
    staffLifecycleEnabled: z.boolean().optional(),
  })
  .strict()

export const actionRequestCommandResultSchema = z
  .object({
    ...actionRequestFields,
    replayClassification: z.enum(['RESOLVED', 'REOPENED', 'EXACT_REPLAY']),
  })
  .strict()
  .refine(lifecycleConsistent, 'Action Request lifecycle is inconsistent')

export type ActionRequestType = z.infer<typeof actionRequestTypeSchema>
// As the repository hands it out: reopenedAtMs and reopenable are explicit.
export type InteractionActionRequest = Omit<
  z.infer<typeof interactionActionRequestSchema>,
  'reopenedAtMs' | 'reopenable'
> & { reopenedAtMs: number | null; reopenable: boolean }
export type InteractionActionRequestList = {
  items: InteractionActionRequest[]
  staffLifecycleEnabled: boolean
}
export type InteractionOperationalState = z.infer<
  typeof interactionOperationalStateSchema
>
